import type { GoogleAutocompletePlaceItem } from 'api/search'
import { getAutocompleteSuggestions } from 'api/search'
import { useCallback, useEffect, useRef, useState } from 'react'

interface AddressSearchParams {
  latitude?: number
  longitude?: number
  radius?: number
}

function useAirportTransferAddressSuggestions(textInput: string, { latitude, longitude, radius }: AddressSearchParams) {
  const [predictions, setPredictions] = useState<Array<GoogleAutocompletePlaceItem>>([])
  const [error, setError] = useState<Error>()
  const [isLoading, setIsLoading] = useState(false)
  const [retryVersion, setRetryVersion] = useState(0)
  const [completedQuery, setCompletedQuery] = useState('')
  const queryIdentity = JSON.stringify([textInput, latitude, longitude, radius, retryVersion])
  const requestIdRef = useRef(0)

  useEffect(() => {
    const requestId = ++requestIdRef.current
    setError(undefined)
    if (!textInput) {
      setPredictions([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    const timeout = setTimeout(() => {
      const hasLatLong = !!(latitude && longitude)
      getAutocompleteSuggestions(textInput, {
        origin: hasLatLong ? `${latitude},${longitude}` : undefined,
        location: hasLatLong ? `${latitude},${longitude}` : undefined,
        radius,
      }).then((results) => {
        if (requestId !== requestIdRef.current) return
        setPredictions(results)
      }).catch((error: Error) => {
        if (requestId !== requestIdRef.current) return
        setPredictions([])
        setError(error)
      }).finally(() => {
        if (requestId !== requestIdRef.current) return
        setIsLoading(false)
        setCompletedQuery(queryIdentity)
      })
    }, 500)

    return () => {
      clearTimeout(timeout)
      requestIdRef.current += 1
    }
  }, [latitude, longitude, queryIdentity, radius, textInput])

  const retry = useCallback(() => setRetryVersion(version => version + 1), [])

  return {
    predictions,
    isLoading: !!textInput && (isLoading || completedQuery !== queryIdentity),
    error: textInput && completedQuery === queryIdentity ? error : undefined,
    retry,
  }
}

export default useAirportTransferAddressSuggestions
